/**
 * Centralized Token Service for handling proactive and reactive token refresh
 *
 * This service provides:
 * - Proactive token refresh: Automatically refreshes tokens before expiration
 * - Reactive token refresh: Fallback refresh on 401 errors
 * - Refresh lock: Ensures only one refresh happens at a time
 * - Request queue: Queues requests while refresh is in progress
 */

import { api } from '@/shared/lib/api'
import { logout, selectExpiresIn, selectRefreshToken, selectToken, updateTokens } from '@/shared/store/auth.store'
import { toast } from '@workspace/ui/components/Sonner'
import { NavigateOptions } from '@tanstack/react-router'

// Navigation handler to avoid circular dependencies
let navigate: ((opts: NavigateOptions) => Promise<void>) | null = null

export const setNavigate = (fn: (opts: NavigateOptions) => Promise<void>) => {
    navigate = fn
}

// Refresh state
let refreshTimer: NodeJS.Timeout | null = null
let isRefreshing = false
let refreshPromise: Promise<string> | null = null

/**
 * Calculate time until token should be refreshed
 * @param expiresIn - Token expiration timestamp in milliseconds
 * @returns Time in milliseconds until refresh should trigger
 */
function calculateRefreshDelay(expiresIn: number): number {
    return expiresIn * 1000
}

/**
 * Proactive refresh: Refresh token before it expires
 * This is the main mechanism to prevent 401 errors
 */
async function performTokenRefresh(): Promise<string> {
    const currentRefreshToken = selectRefreshToken()

    if (!currentRefreshToken) {
        console.error('[TokenService] No refresh token available')
        handleRefreshFailure('No refresh token available')
        throw new Error('No refresh token available')
    }

    try {
        console.log('[TokenService] Refreshing access token...')

        const response = await api.auth.refresh({
            refreshToken: currentRefreshToken,
        })

        // Update tokens in store
        updateTokens(
            response.token.accessToken, 
            response.token.expiresIn,
            response.refreshToken.refreshToken
        )

        console.log('[TokenService] Token refreshed successfully')

        // Schedule next refresh
        scheduleProactiveRefresh(response.token.expiresIn)

        return response.token.accessToken
    } catch (error) {
        console.error('[TokenService] Token refresh failed:', error)
        handleRefreshFailure('Token refresh failed')
        throw error
    }
}

/**
 * Refresh with lock mechanism - ensures only one refresh happens at a time
 * Other requests wait for the ongoing refresh to complete
 */
export async function refreshWithLock(): Promise<string> {
    // If refresh is already in progress, wait for it
    if (isRefreshing && refreshPromise) {
        console.log('[TokenService] Refresh already in progress, waiting...')
        return refreshPromise
    }

    // Start new refresh
    isRefreshing = true
    refreshPromise = performTokenRefresh()
        .then(token => {
            isRefreshing = false
            refreshPromise = null
            return token
        })
        .catch(error => {
            isRefreshing = false
            refreshPromise = null
            throw error
        })

    return refreshPromise
}

/**
 * Schedule proactive token refresh based on expiration time
 * This implements the time-based refresh strategy
 */
export function scheduleProactiveRefresh(expiresIn: number): void {
    // Clear existing timer
    clearProactiveRefresh()

    // Calculate delay until refresh should trigger
    const delay = calculateRefreshDelay(expiresIn)

    console.log(`[TokenService] Scheduling proactive refresh in ${Math.round(delay / 1000)}s`)

    // Schedule refresh
    refreshTimer = setTimeout(async () => {
        try {
            await refreshWithLock()
        } catch (error) {
            await logout()
            // Error already handled in performTokenRefresh
            console.error('[TokenService] Proactive refresh failed:', error)
            if (navigate) {
                navigate({ to: '/login' })
            } else {
                // Fallback if navigate hasn't been injected yet
                window.location.href = '/login'
            }
        }
    }, delay)
}

/**
 * Clear proactive refresh timer
 * Should be called on logout
 */
export function clearProactiveRefresh(): void {
    if (refreshTimer) {
        clearTimeout(refreshTimer)
        refreshTimer = null
        console.log('[TokenService] Proactive refresh timer cleared')
    }
}

/**
 * Initialize token service with current token expiration
 * Should be called on app mount and login
 */
export function initializeTokenService(): void {
    const expiresIn = selectExpiresIn()
    const token = selectToken()

    if (token && expiresIn) {
        console.log('[TokenService] Initializing token service')
        scheduleProactiveRefresh(expiresIn)
    }
}

/**
 * Handle refresh failure
 * Clears auth data, shows notification, and redirects to login
 */
function handleRefreshFailure(reason: string): void {
    console.log(`[TokenService] Handling refresh failure: ${reason}`)

    // Clear refresh timer
    clearProactiveRefresh()

    // Clear auth state (fire-and-forget since we're in a sync context)
    logout().catch(err => console.error('[TokenService] Logout failed:', err))

    // Show notification
    toast.error({
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
    })

    if (navigate) {
        navigate({ to: '/login' })
    } else {
        // Fallback if navigate hasn't been injected yet
        window.location.href = '/login'
    }

    // Redirect to login (will be handled by router guard)
    // The beforeLoad in __root.tsx will handle the redirect
}
